/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, ReactNode, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DocumentsService, DocumentDto } from '@/api/portal'
import { useOrders } from '@/contexts/useOrders'

export interface DocumentsContextValue {
    documents: DocumentDto[]
    documentsByDdoId: Map<string, DocumentDto[]>
    isLoading: boolean
}

export const DocumentsContext = createContext<DocumentsContextValue | undefined>(undefined)

interface DocumentsProviderProps {
    children: ReactNode
}

export const DocumentsProvider = ({ children }: DocumentsProviderProps) => {
    const { orders, lastUpdated } = useOrders()

    // Memoize ddoIds to avoid unnecessary recalculations - sort for stable queryKey
    const ddoIds = useMemo(() => {
        const ids = orders.map(order => order.id).filter(Boolean) as string[]
        return ids.sort()
    }, [orders])

    // Use a stable query key based on sorted ddoIds
    const { data: documents = [], isLoading } = useQuery({
        queryKey: ['documents', ddoIds, lastUpdated],
        queryFn: async () => {
            if (ddoIds.length === 0) return []
            return await DocumentsService.documentControllerGetDocumentsByDdoIds({
                ddoIds,
            })
        },
        enabled: ddoIds.length > 0,
        // Refetch when orders are refreshed
        refetchOnMount: true,
    })

    const documentsByDdoId = useMemo(() => {
        const map = new Map<string, DocumentDto[]>()
        documents.forEach(doc => {
            const existing = map.get(doc.ddoId) || []
            existing.push(doc)
            map.set(doc.ddoId, existing)
        })
        return map
    }, [documents])

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo<DocumentsContextValue>(
        () => ({
            documents,
            documentsByDdoId,
            isLoading,
        }),
        [documents, documentsByDdoId, isLoading],
    )

    return <DocumentsContext.Provider value={value}>{children}</DocumentsContext.Provider>
}

export const useDocuments = () => {
    const context = useContext(DocumentsContext)
    if (!context) {
        throw new Error('useDocuments must be used within a DocumentsProvider')
    }
    return context
}
